import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiPost, getApiBase } from '../../services/api';
import { passwordMeetsStrength, passwordStrengthChecks } from '../../utils/passwordStrength';

export default function ResetPassword() {
  const apiBase = getApiBase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') || '';
  const emailParam = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordMismatchError, setPasswordMismatchError] = useState(false);
  const [passwordStrengthSubmitAttempt, setPasswordStrengthSubmitAttempt] = useState(false);
  const [genericError, setGenericError] = useState('');

  useEffect(() => {
    if (!token || !emailParam) {
      navigate('/forgot-password', { replace: true });
    }
  }, [token, emailParam, navigate]);

  if (!token || !emailParam) {
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFieldErrors({});
    setPasswordMismatchError(false);
    setPasswordStrengthSubmitAttempt(false);
    setTokenError(false);
    setGenericError('');

    if (!passwordMeetsStrength(password)) {
      setPasswordStrengthSubmitAttempt(true);
      return;
    }
    if (password !== passwordConfirmation) {
      setPasswordMismatchError(true);
      return;
    }

    if (!apiBase) {
      setGenericError('Password reset is unavailable.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiPost('/api/v1/auth/password/reset', {
        token,
        email: emailParam,
        password,
        password_confirmation: passwordConfirmation,
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 422 && data.errors) {
        if (data.errors.email) {
          setTokenError(true);
          return;
        }
        setFieldErrors(data.errors);
        return;
      }
      if (!res.ok) {
        setGenericError(data.message || 'Something went wrong. Please try again.');
        return;
      }

      navigate('/login?reset=1', { replace: true });
    } finally {
      setSubmitting(false);
    }
  }

  const checks = passwordStrengthChecks(password);
  const allStrengthMet = passwordMeetsStrength(password);
  const showPasswordRequirements =
    !allStrengthMet || passwordFocused || passwordStrengthSubmitAttempt;

  return (
    <div className="rwm-auth rwm-reset-password bg-white">
      <div className="rwm-auth__card py-5">
        <Card className="rwm-reset-password__card border" style={{ borderColor: 'var(--rwm-border)' }}>
          <Card.Body className="p-4">
            <h1 className="h4 fw-semibold mb-4 rwm-reset-password__title" style={{ color: 'var(--rwm-dark)' }}>
              Set New Password
            </h1>

            {tokenError ? (
              <Alert variant="danger" data-testid="rwm-reset-error">
                This reset link is invalid or has expired.{' '}
                <Link to="/forgot-password" className="alert-link">
                  Request a new reset link
                </Link>
              </Alert>
            ) : (
              <Form data-testid="rwm-reset-form" onSubmit={handleSubmit} noValidate>
                <Form.Group className="mb-3" controlId="resetEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={emailParam}
                    readOnly
                    className="rwm-reset-password__email-readonly"
                    data-testid="rwm-reset-email"
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="resetPassword">
                  <Form.Label>New password</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(ev) => {
                        const next = ev.target.value;
                        setPassword(next);
                        setPasswordStrengthSubmitAttempt(false);
                        if (passwordMismatchError && next === passwordConfirmation) {
                          setPasswordMismatchError(false);
                        }
                      }}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      required
                      autoComplete="new-password"
                      data-testid="rwm-reset-password"
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  {fieldErrors.password ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.password[0]}</div>
                  ) : null}
                  {showPasswordRequirements ? (
                    <ul
                      className="list-unstyled small mb-0 mt-2"
                      data-testid="rwm-reset-password-requirements"
                    >
                      <li style={{ color: checks.length ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                        {checks.length ? '✓' : '✗'} At least 8 characters
                      </li>
                      <li style={{ color: checks.upper ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                        {checks.upper ? '✓' : '✗'} One uppercase letter
                      </li>
                      <li style={{ color: checks.lower ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                        {checks.lower ? '✓' : '✗'} One lowercase letter
                      </li>
                      <li style={{ color: checks.number ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                        {checks.number ? '✓' : '✗'} One number
                      </li>
                      <li style={{ color: checks.special ? 'var(--rwm-success)' : 'var(--rwm-slate)' }}>
                        {checks.special ? '✓' : '✗'} One special character (for example !, @, #, $)
                      </li>
                    </ul>
                  ) : null}
                </Form.Group>

                <Form.Group className="mb-3" controlId="resetPasswordConfirm">
                  <Form.Label>Confirm password</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                      type={showPassword2 ? 'text' : 'password'}
                      value={passwordConfirmation}
                      onChange={(ev) => {
                        const v = ev.target.value;
                        setPasswordConfirmation(v);
                        if (passwordMismatchError && v === password) {
                          setPasswordMismatchError(false);
                        }
                      }}
                      onBlur={() => {
                        if (!passwordConfirmation) {
                          setPasswordMismatchError(false);
                          return;
                        }
                        setPasswordMismatchError(password !== passwordConfirmation);
                      }}
                      required
                      autoComplete="new-password"
                      data-testid="rwm-reset-confirm"
                    />
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      className="flex-shrink-0"
                      onClick={() => setShowPassword2((v) => !v)}
                      aria-label={showPassword2 ? 'Hide confirm password' : 'Show confirm password'}
                    >
                      {showPassword2 ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  {fieldErrors.password_confirmation ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.password_confirmation[0]}</div>
                  ) : passwordMismatchError ? (
                    <div className="rwm-auth__field-error mt-1" data-testid="rwm-reset-password-mismatch-error">
                      Passwords do not match.
                    </div>
                  ) : null}
                </Form.Group>

                {genericError ? (
                  <p className="rwm-auth__field-error mb-3">{genericError}</p>
                ) : null}

                <Button
                  type="submit"
                  variant="info"
                  className="w-100 text-white btn-lg"
                  disabled={submitting}
                  data-testid="rwm-reset-submit"
                >
                  {submitting ? 'Resetting…' : 'Reset Password'}
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
